import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { Link, useNavigate } from "react-router-dom";
import { toastr } from 'react-redux-toastr';
import { useTranslation } from 'react-i18next';

import CustomButton from '../../../CustomButton';

import { getUserInfo } from '../../../../action/ClientActions';
import { loginUser, logoutUser } from '../../../../action/AccountActions';
import { useAuth } from "../../../../../../context/AuthContext";

import '../../../dump/menuComponents/menuComponents.sass';

const LoginForm = (props) => {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const { login, logout } = useAuth();
	const { t } = useTranslation('translation', { keyPrefix: 'loginForms.loginForm' });
	const [data, setData] = useState({
		email: '',
		password: '',
	})
	const [errMessage, setErrMessage] = useState({
		email: '',
		password: '',
	})
	const [toggle_password, setToggle_password] = useState(true)
	const [rememberMe, setRememberMe] = useState(1)

	useEffect(() => {
		setRememberMe(localStorage.getItem('r_u') === null ? 1 : localStorage.getItem('r_u') * 1);
	}, []);

	const toggleLoader = useCallback((show) => {
		dispatch({ type: 'TOGGLE_LOADER', payload: show });
	}, [dispatch]);

	const togglePassword = () => {
		setToggle_password(!toggle_password);
	}

	const fillForm = ({ target }) => {
		const value = target.type === 'checkbox' ? target.checked : target.value;

		if (target.name === 'rememberMe') {
			setRememberMe(value * 1);
		} else {
			let dataUpdate = data;
			dataUpdate[target.name] = value;
			setData({ ...dataUpdate, [target.name]: value });
			setErrMessage({ ...errMessage, email: '', password: '' });
		}
	}

	const checkForm = (data) => {
		let error = false;
		const updatedErrMessage = { ...errMessage };

		for (const i in data) {
			if (data[i] == '') {
				error = true;
				updatedErrMessage[i] = t('errEnterYour') + i;
			}
			else if (errMessage[i]) {
				updatedErrMessage[i] = null;
			}
		}

		if (!updatedErrMessage.email) {
			const re = /^([a-zA-Z0-9_\-\.\+]+)@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
			if (!re.test(data.email)) {
				error = true;
				updatedErrMessage.email = t('errEmail');
			}
		}

		setErrMessage(updatedErrMessage);
		return error;
	}

	const sendForm = (e) => {
		e.preventDefault()

		const error = checkForm(data)

		if (!error) {
			toggleLoader(true);

			loginUser(data)
				.then(async (res, err) => {
					if (res.data.response.error && res.data.response.error === 'you have to logout first') {
						logoutUser()
							.then(async (res, err) => {
								await logout();
								sendForm(e);
							})
							.catch((error) => {
								console.log('!error');
								console.log(error);
							});
					}
					else if (res.data.response.error && ['Invalid password', 'Specified user does not exist'].includes(res.data.response.error)) {
						toggleLoader(false)
						setErrMessage({ ...errMessage, password: t('errEmail/Password') })
					}
					else if (res.data.response.error) {
						toggleLoader(false)
						toastr.error(t('unableLoginUser'));
						console.log("Error: ", res.data)
					}
					else if (res.data.response.roles && res.data.response.roles[0] === 'notification_recipient') {
						toggleLoader(false)
						navigate('/confirmation');
					}
					else if (res.data.response.mfa_required) {
						toggleLoader(false)
						if (res.data.response.mfa_method) {
							// User already has an MFA method — go straight to code-entry screen
							navigate('/mfa-verify', {
								state: {
									token: res.data.response.token,
									verifyMethod: res.data.response.mfa_method,
								}
							});
						} else {
							// First login: user has no MFA method yet — show method-selection screen
							navigate('/mfa-settings', {
								state: {
									token: res.data.response.token,
								}
							});
						}
					}
					else {
						// ToDo: Cognito - disable
						const { session_token } = res.data.response
						// const { auth_refresh_token, auth_token } = res.data.response;
						// await login({ auth_refresh_token, auth_token });
						await login({ session_token });
						// FWSD-6093, checks if EULA was accepted otherwise redirect to /eula-agreement
						getUserInfo()
							.then(resUserInfo => {
								toggleLoader(false);
								if (resUserInfo.data.response.user.eula_was_viewed) {
									navigate('/', { replace: true });
								} else {
									navigate('/eula-agreement', {
										replace: true,
										state: {
											isLoginMode: true
										}
									});
								}
							})
							.catch(errorUserInfo => {
								toggleLoader(false)
								console.log('getUserInfo error: ', errorUserInfo.response, errorUserInfo)
								toastr.error(errorUserInfo)
							})
					}
				})
				.catch((error) => {
					console.log(error.response);

					toggleLoader(false)

					if (error.response && error.response.data.response.error) {
						let error_content = error.response.data.response.error;
						let error_title = '';
						if (error_content.toLowerCase() === 'invalid password') {
							error_title = t('errorTitle_1');
							error_content = t('errorContent_1');
						}
						else if (error_content.toLowerCase() === 'specified user does not exist') {
							error_title = t('errorTitle_2');
							error_content = t('errorContent_2');
						}

						toastr.error(error_content);
					}
					else {
						toastr.error(t('unableLoginUser'));
					}
				});
		}
	}

	const { email, password } = errMessage;

	return (
		<div className="panel-body">
			<h3 className="login-header">{t('headerLogin')}</h3>
			<div className="form-wrapper">

				<form onSubmit={sendForm} autoComplete={rememberMe ? 'auto' : 'off'} id="login-form">
					{!rememberMe && <input autoComplete="false" name="hidden" type="text" className="hidden" hidden="none" />}
					<div className='login-form'>
						<label className={`login-label ${(data.email.length) ? 'active' : ''}`}>{t('labelE-mail')}</label>
						<input
							type="text"
							name="email"
							className={`vehicle-search ${(email || password) ? 'error' : ''}`}
							onChange={fillForm}
							placeholder={t('placeholderE-Mail')}
							autoComplete={rememberMe ? 'auto' : 'new-password'}
						/>
						{email && <span className="help-block error-label">{email}</span>}
					</div>
					<div className="login-form has-feedback">
						<label className={`login-label ${(data.password.length) ? 'active' : ''}`}>{t('labelPassword')}</label>
						<input
							type={toggle_password ? 'password' : 'text'}
							id="password"
							name="password"
							className={`vehicle-search ${password ? 'error' : ''}`}
							data-toggle="password"
							onChange={fillForm}
							placeholder={t('placeholderPassword')}
							autoComplete={rememberMe ? 'auto' : 'new-password'}
						/>
						<span
							className={`fas ${toggle_password ? 'fa-eye' : 'fa-eye-slash'} form-control-feedback`}
							onClick={togglePassword}
						/>
						{password && <span className="help-block error-label">{password}</span>}
					</div>
					<div className="login-form">
						<div className="checkbox">
							<label>
								<input
									type="checkbox"
									name="rememberMe"
									onChange={fillForm}
								/>
								{t('labelRememberMe')}
							</label>
						</div>
					</div>
					<div className="login-form submit-btn">
						<CustomButton variant="primary" type="submit" value="Login" id="login-button" style={{ padding: '8px 0px' }}>{t('btnLogin')}</CustomButton>
					</div>

					<div className="forgot-password">
						<Link to="/restore-password">
							{t('linkForgotPassword')}
						</Link>
					</div>
					<div className="forgot-password">
						<Link to="/mfa-settings">
							{t('linkMfaSettings')}
						</Link>
					</div>
				</form>

			</div>
		</div>
	)
}

export default LoginForm;
